/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState } from 'react';
import { useFocusWithin } from 'react-aria';
import PropTypes from 'prop-types';
import { InputWrapper, IconWrapper, ArrowDownIcon, DashedUnderline } from '../Select/styledComponents.js';
import { noop } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { DROPDOWN_VARIANT } from './constants.js';
import { DropdownInput } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';

var _excluded = ["ariaDescribedBy", "ariaLabel", "ariaLabelledBy", "ariaRequired", "className", "customStyle", "disabled", "dropdownItems", "id", "label", "onChange", "outline", "selected", "style", "variant"];
var DropdownSelect = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var ariaDescribedBy = _ref.ariaDescribedBy,
    ariaLabel = _ref.ariaLabel,
    ariaLabelledBy = _ref.ariaLabelledBy,
    ariaRequired = _ref.ariaRequired,
    className = _ref.className,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      dropdownInputStyle: {}
    } : _ref$customStyle,
    disabled = _ref.disabled,
    _ref$dropdownItems = _ref.dropdownItems,
    dropdownItems = _ref$dropdownItems === undefined ? [] : _ref$dropdownItems,
    controlledId = _ref.id,
    label = _ref.label,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    outline = _ref.outline,
    _ref$selected = _ref.selected,
    selected = _ref$selected === undefined ? 0 : _ref$selected,
    style = _ref.style,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? DROPDOWN_VARIANT.BASIC : _ref$variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var _useDerivedState = useDerivedState(useId('dropdown-select-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var _safeCustomStyle = safeCustomStyle(customStyle),
    dropdownInputStyle = _safeCustomStyle.dropdownInputStyle;
  var value = dropdownItems[selected] ? dropdownItems[selected].value : '';
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var _useFocusWithin = useFocusWithin({
      isDisabled: disabled,
      onFocusWithinChange: function onFocusWithinChange(isFocusWithin) {
        return setIsFocused(isFocusWithin);
      }
    }),
    focusWithinProps = _useFocusWithin.focusWithinProps;
  return /*#__PURE__*/jsxs(InputWrapper, _objectSpread(_objectSpread({}, focusWithinProps), {}, {
    "aria-invalid": outline,
    "data-disabled": disabled,
    "data-hasicon": true,
    "data-focused": isFocused,
    "data-variant": variant,
    style: style,
    theme: theme,
    children: [/*#__PURE__*/jsx(DropdownInput, _objectSpread(_objectSpread({
      as: "select",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy || (label ? "".concat(id, "-label") : undefined),
      "aria-required": ariaRequired,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": outline,
      className: "mux-Dropdown-Input ".concat(className),
      "data-disabled": disabled,
      "data-testid": "dropdown-native-test",
      "data-value": value,
      dropdownInputStyle: dropdownInputStyle,
      disabled: disabled,
      error: outline,
      id: "".concat(id, "-toggle"),
      onChange: onChange,
      ref: ref,
      theme: theme,
      value: value
    }, rest), {}, {
      children: dropdownItems.map(function (elem) {
        return /*#__PURE__*/jsx("option", {
          id: "".concat(id, "-item-").concat(elem.value),
          value: elem.value,
          disabled: elem.disabled,
          "data-selected": value === elem.value,
          children: elem.label
        }, "item-".concat(elem.value));
      })
    })), /*#__PURE__*/jsx(IconWrapper, {
      "aria-hidden": true,
      children: /*#__PURE__*/jsx(ArrowDownIcon, {})
    }), variant === DROPDOWN_VARIANT.BASIC_LINE_DASHED ? /*#__PURE__*/jsx(DashedUnderline, {
      "aria-hidden": true,
      theme: theme
    }) : null]
  }));
});
process.env.NODE_ENV !== "production" ? DropdownSelect.propTypes = {
  /** ID of DOM element for dropdown */
  id: PropTypes.string,
  /** label component for input */
  label: PropTypes.node,
  /** flag to show/hide error outline */
  outline: PropTypes.bool,
  /** flag to disable dropdown */
  disabled: PropTypes.bool,
  /** index of selected item, default is 0 */
  selected: PropTypes.number,
  /** override input aria-label */
  ariaLabel: PropTypes.string,
  /** override input aria-labelledby */
  ariaLabelledBy: PropTypes.string,
  /** global attribute aria-required */
  ariaRequired: PropTypes.bool,
  /** aria-describedby attribute */
  ariaDescribedBy: PropTypes.string,
  /** function to call onClick of a dropdown item */
  handleItemClick: PropTypes.func,
  /** Dropdown menu items */
  dropdownItems: PropTypes.arrayOf(PropTypes.shape({
    /** value for the item, equivalent to option value in select element */
    value: PropTypes.string.isRequired,
    /** label for the item, equivalent to option label in select element */
    label: PropTypes.string.isRequired,
    /** Item is not selectable if it is disabled */
    disabled: PropTypes.bool
  })),
  /**
   * Inline style overrides (needed to be able to specify CSS variables)
   */
  style: PropTypes.object,
  /** Which style of input to use */
  variant: PropTypes.oneOf([DROPDOWN_VARIANT.BASIC, DROPDOWN_VARIANT.BASIC_LINE, DROPDOWN_VARIANT.BASIC_LINE_DASHED]),
  /** custom styles */
  customStyle: PropTypes.shape({
    dropdownInputStyle: PropTypes.object // styling for Dropdown Input items
  })
} : undefined;
process.env.NODE_ENV !== "production" ? DropdownSelect.displayName = 'DropdownSelect' : undefined;

export { DropdownSelect as default };
